import React, { useState } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import Modal from 'components/common/Modal'
import DeliveryAddress from './DeliveryAddress'
import usePostingNoticeRequestContext from 'hooks/posting_notice_request/usePostingNoticeRequestContext'
import useNotification from 'hooks/context/useNotification'
import Loader from 'components/Loader'

function ConfirmationModal({
  open,
  onClose,
  postingNoticeRequest,
  isMail,
  isEmail,
  onSubmit,
  onDownload,
  hasError,
}) {
  const [loading, setLoading] = useState(false)
  const { updatePostingNoticeRequest, setShippingAddress } =
    usePostingNoticeRequestContext()
  const { setError } = useNotification()

  const getConfirmButtonText = () => {
    if (hasError) {
      return 'Send as Mail'
    }

    return `Send ${isMail ? 'Mail' : ''} ${isEmail ? 'Email' : ''}`
  }

  return (
    <Modal
      title="Success"
      size={hasError ? '' : 'md'}
      scrollable={false}
      open={open}
      onClose={() => {}}
      hideButtonsBorder={hasError}
      footerButtonProps={
        hasError
          ? []
          : [
              {
                children: getConfirmButtonText(),
                variant: 'contained',
                color: 'primary',
                disabled: loading,
                onClick: () => {
                  if (loading) return
                  setLoading(true)
                  onSubmit(postingNoticeRequest?.PostingNoticeRequestID)
                    .then(() => {
                      onClose()
                    })
                    .finally(() => {
                      setLoading(false)
                    })
                },
              },
              {
                children: 'Download PDF',
                variant: 'outlined',
                color: 'primary',
                disabled: loading,
                onClick: () => {
                  if (loading) return
                  setLoading(true)
                  onDownload(postingNoticeRequest?.PostingNoticeRequestID)
                    .then(() => {
                      onClose()
                    })
                    .finally(() => {
                      setLoading(false)
                    })
                },
              },
            ]
      }
    >
      <Box sx={{ height: '100%' }}>
        <Typography variant="h2" fontWeight="medium" my={6}>
          Posting Notice Request #
          {postingNoticeRequest?.PostingNoticeRequestPubID} has been
          successfully created.
        </Typography>
        {loading ? (
          <Loader />
        ) : (
          <>
            {hasError && (
              <Alert severity="error">
                <Typography variant="body1">
                  The Generated PDF has a file size greater than 10MB. Please
                  download the PDF or convert the submission to mail delivery.
                </Typography>
              </Alert>
            )}
            {hasError ? (
              <Box py={6}>
                <DeliveryAddress
                  disableEmail
                  compact
                  onNext={async (values) => {
                    try {
                      setLoading(true)
                      setShippingAddress(values)

                      await updatePostingNoticeRequest(
                        postingNoticeRequest?.PostingNoticeRequestID
                      )

                      await onSubmit(
                        postingNoticeRequest?.PostingNoticeRequestID
                      )

                      onClose()
                    } catch (error) {
                      setError(
                        'Failed to update posting notice request. Please try again.',
                        error
                      )
                    } finally {
                      setLoading(false)
                    }
                  }}
                  footerButtonProps={{
                    isConfirmModal: true,
                    onDownload: async () => {
                      setLoading(true)
                      try {
                        await onDownload(
                          postingNoticeRequest?.PostingNoticeRequestID
                        )
                      } finally {
                        setLoading(false)
                      }
                    },
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body1" my={6}>
                You can now download the PDF of the posting notice request or{' '}
                {isMail ? 'mail' : ''} {isEmail ? 'email' : ''} the posting
                notice request to its recipient(s).
              </Typography>
            )}
          </>
        )}
      </Box>
    </Modal>
  )
}

export default ConfirmationModal
